<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DOCUMENT_TYPE, type DocumentType } from '@hsl-ems/contracts';

import { p1Api, type UserListItem } from '@/lib/p1-api';
import { p3Api, type ApprovalTaskDetail } from '@/lib/p3-api';

type Decision = 'approve' | 'return' | 'reject';

const route = useRoute();
const router = useRouter();
const task = ref<ApprovalTaskDetail>();
const currentUserId = ref('');
const loading = ref(true);
const deciding = ref(false);
const error = ref('');
const notice = ref('');
const selectedDecision = ref<Decision>();
const comment = ref('');
const canTransfer = ref(false);
const users = ref<UserListItem[]>([]);
const transferTargetUserId = ref('');
const transferReason = ref('');
const operating = ref(false);

const taskId = computed(() => String(route.params.id ?? ''));
const canDecide = computed(
  () =>
    task.value?.status === 'PENDING' &&
    task.value.instance.status === 'RUNNING' &&
    task.value.assignee.id === currentUserId.value,
);
const canUrge = computed(
  () =>
    task.value?.status === 'PENDING' &&
    task.value.instance.status === 'RUNNING' &&
    task.value.instance.document.applicantUserId === currentUserId.value,
);
const transferCandidates = computed(() =>
  users.value.filter((user) => user.status === 'ACTIVE' && user.id !== task.value?.assignee.id),
);

const typeLabels: Record<DocumentType, string> = {
  [DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT]: '费用报销',
  [DOCUMENT_TYPE.LOAN]: '借款申请',
  [DOCUMENT_TYPE.CASH_REPAYMENT]: '现金还款',
  [DOCUMENT_TYPE.LOAN_OFFSET]: '借款冲销',
  [DOCUMENT_TYPE.CORPORATE_PAYMENT]: '对公付款',
};
const slugs: Record<DocumentType, string> = {
  [DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT]: 'reimbursement',
  [DOCUMENT_TYPE.LOAN]: 'loan',
  [DOCUMENT_TYPE.CASH_REPAYMENT]: 'cash-repayment',
  [DOCUMENT_TYPE.LOAN_OFFSET]: 'loan-offset',
  [DOCUMENT_TYPE.CORPORATE_PAYMENT]: 'corporate-payment',
};
const taskStatusLabels = {
  PENDING: '待处理',
  APPROVED: '已通过',
  RETURNED: '已退回',
  REJECTED: '已驳回',
  SKIPPED: '已跳过',
  CANCELLED: '已取消',
} as const;
const actionLabels = {
  APPROVE: '通过',
  RETURN: '退回修改',
  REJECT: '驳回',
  WITHDRAW: '撤回',
  TRANSFER: '转交',
  URGE: '催办',
} as const;

onMounted(load);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const [me, detail] = await Promise.all([p1Api.me(), p3Api.task(taskId.value)]);
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    currentUserId.value = me.userId;
    canTransfer.value =
      me.permissions.includes('approval:transfer') && me.roleCodes.includes('GROUP_ADMIN');
    if (canTransfer.value && me.permissions.includes('user:read'))
      users.value = await p1Api.users();
    task.value = detail;
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : '审批详情加载失败';
    if (message.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = message;
  } finally {
    loading.value = false;
  }
}

async function urgeTask() {
  if (!task.value || !canUrge.value) return;
  operating.value = true;
  error.value = '';
  try {
    await p3Api.urge(task.value.id, task.value.version);
    await load();
    notice.value = '已催办当前处理人；24 小时内不可重复催办';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '催办失败';
  } finally {
    operating.value = false;
  }
}

async function transferTask() {
  if (!task.value || !canTransfer.value) return;
  if (!transferTargetUserId.value || transferReason.value.trim().length < 2) {
    error.value = '请选择新处理人，并填写至少 2 个字的转交原因';
    return;
  }
  if (!window.confirm('转交会立即变更当前待办处理人，确定继续吗？')) return;
  operating.value = true;
  error.value = '';
  try {
    await p3Api.transfer(
      task.value.id,
      task.value.version,
      transferTargetUserId.value,
      transferReason.value.trim(),
    );
    transferTargetUserId.value = '';
    transferReason.value = '';
    await load();
    notice.value = '待办已转交，原处理人与新处理人已写入审批历史';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '转交失败';
  } finally {
    operating.value = false;
  }
}

function chooseDecision(action: Decision) {
  selectedDecision.value = action;
  comment.value = '';
  notice.value = '';
}

async function submitDecision() {
  if (!task.value || !selectedDecision.value || !canDecide.value) return;
  if (
    (selectedDecision.value === 'return' || selectedDecision.value === 'reject') &&
    comment.value.trim().length < 2
  ) {
    error.value = '退回或驳回必须填写至少 2 个字的原因';
    return;
  }
  if (
    selectedDecision.value === 'reject' &&
    !window.confirm('驳回后原单终止且不能恢复，确定继续吗？')
  )
    return;
  deciding.value = true;
  error.value = '';
  try {
    await p3Api.decide(
      task.value.id,
      selectedDecision.value,
      task.value.version,
      comment.value.trim() || undefined,
    );
    const label =
      selectedDecision.value === 'approve'
        ? '已通过当前任务'
        : selectedDecision.value === 'return'
          ? '已退回申请人修改'
          : '已驳回申请';
    selectedDecision.value = undefined;
    comment.value = '';
    await load();
    notice.value = label;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '审批操作失败';
  } finally {
    deciding.value = false;
  }
}

function snapshotValue(snapshot: Record<string, unknown> | null, key: string) {
  const value = snapshot?.[key];
  return typeof value === 'string' ? value : '—';
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}

function dateTime(value: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
</script>

<template>
  <main class="workspace-shell approval-detail-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/approval-tasks">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>审批详情</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink class="active" to="/approval-tasks">审批待办</RouterLink>
        <RouterLink to="/admin">组织与权限</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/approval-tasks">← 返回工作台</RouterLink>
    </header>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="notice" class="inline-alert inline-alert--success" role="status">{{ notice }}</p>
    <section v-if="loading" class="document-empty">正在加载审批详情…</section>

    <template v-else-if="task">
      <section class="approval-hero">
        <div>
          <p class="eyebrow">{{ typeLabels[task.instance.document.type] }}</p>
          <h1>{{ task.instance.document.documentNo }}</h1>
          <p>{{ task.instance.document.description }}</p>
        </div>
        <div class="approval-amount">
          <small>申请金额</small>
          <strong>{{ money(task.instance.document.requestedAmount) }}</strong>
          <span class="status-pill" :data-status="task.status">
            {{ taskStatusLabels[task.status] }}
          </span>
        </div>
      </section>

      <section class="approval-detail-grid">
        <article class="form-section approval-summary-card">
          <h2>申请快照</h2>
          <dl class="snapshot-list">
            <div>
              <dt>申请人</dt>
              <dd>{{ snapshotValue(task.instance.document.applicantSnapshot, 'name') }}</dd>
            </div>
            <div>
              <dt>归属组织</dt>
              <dd>{{ snapshotValue(task.instance.document.organizationSnapshot, 'name') }}</dd>
            </div>
            <div>
              <dt>单据类型</dt>
              <dd>{{ typeLabels[task.instance.document.type] }}</dd>
            </div>
            <div>
              <dt>提交轮次</dt>
              <dd>第 {{ task.instance.submissionNumber }} 次</dd>
            </div>
          </dl>
          <RouterLink
            class="quiet-link"
            :to="`/documents/${slugs[task.instance.document.type]}/${task.instance.document.id}`"
          >
            查看单据与附件
          </RouterLink>
        </article>

        <article class="form-section current-task-card">
          <p class="eyebrow">当前节点 {{ task.node.sequence }}</p>
          <h2>{{ task.node.name }}</h2>
          <p>
            {{
              task.node.approvalMode === 'ANY_ONE'
                ? '任一候选人通过即可推进'
                : '全部候选人通过后推进'
            }}
          </p>
          <dl class="snapshot-list">
            <div>
              <dt>当前处理人</dt>
              <dd>{{ task.assignee.name }}</dd>
            </div>
            <div>
              <dt>期望完成</dt>
              <dd>{{ dateTime(task.dueAt) }}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section v-if="canDecide" class="decision-panel">
        <div class="decision-buttons" aria-label="审批操作">
          <button type="button" @click="chooseDecision('approve')">通过</button>
          <button class="quiet-button" type="button" @click="chooseDecision('return')">
            退回修改
          </button>
          <button class="danger-button" type="button" @click="chooseDecision('reject')">
            驳回
          </button>
        </div>
        <form v-if="selectedDecision" class="decision-form" @submit.prevent="submitDecision">
          <label>
            <span>{{
              selectedDecision === 'approve' ? '审批意见（选填）' : '处理原因（必填）'
            }}</span>
            <textarea
              v-model="comment"
              rows="4"
              maxlength="1000"
              :required="selectedDecision !== 'approve'"
              placeholder="意见会写入不可修改的审批历史"
            />
          </label>
          <div>
            <button type="button" class="text-button" @click="selectedDecision = undefined">
              取消
            </button>
            <button type="submit" :disabled="deciding">
              {{ deciding ? '正在提交…' : '确认操作' }}
            </button>
          </div>
        </form>
      </section>

      <section v-if="canUrge || canTransfer" class="form-section approval-operations-panel">
        <div v-if="canUrge" class="operation-block">
          <div>
            <h2>催办当前任务</h2>
            <p>催办仅发送提醒并记录历史，不改变审批状态；同一任务 24 小时内限一次。</p>
          </div>
          <button class="quiet-button" type="button" :disabled="operating" @click="urgeTask">
            {{ operating ? '正在处理…' : '发送催办' }}
          </button>
        </div>
        <form
          v-if="canTransfer"
          class="operation-block transfer-form"
          @submit.prevent="transferTask"
        >
          <div>
            <h2>管理员转交</h2>
            <p>候选人仍由服务端校验其审批角色和组织范围，不能转给无权限人员。</p>
          </div>
          <label>
            <span>新处理人</span>
            <select v-model="transferTargetUserId" required>
              <option value="" disabled>请选择</option>
              <option v-for="user in transferCandidates" :key="user.id" :value="user.id">
                {{ user.name }} ·
                {{ user.passwordCredential?.username || user.employeeNo || '无账号名' }}
              </option>
            </select>
          </label>
          <label class="wide-field">
            <span>转交原因</span>
            <textarea v-model="transferReason" rows="3" maxlength="1000" required />
          </label>
          <button type="submit" :disabled="operating">确认转交</button>
        </form>
      </section>

      <section class="form-section approval-history">
        <h2>节点任务</h2>
        <div class="timeline">
          <article v-for="item in task.instance.tasks" :key="item.id">
            <span class="timeline-dot" :data-status="item.status" />
            <div>
              <strong>{{ item.node.sequence }}. {{ item.node.name }}</strong>
              <p>{{ item.assignee.name }} · {{ taskStatusLabels[item.status] }}</p>
              <small>{{ dateTime(item.decidedAt ?? item.createdAt) }}</small>
            </div>
          </article>
        </div>

        <h2>操作记录</h2>
        <div v-if="task.instance.actions.length" class="timeline">
          <article v-for="action in task.instance.actions" :key="action.id">
            <span class="timeline-dot" :data-status="action.afterStatus" />
            <div>
              <strong>{{ action.actor.name }} · {{ actionLabels[action.action] }}</strong>
              <p v-if="action.transferTarget">
                {{ action.transferSource?.name ?? '原处理人' }} → {{ action.transferTarget.name }}
              </p>
              <p v-if="action.comment">{{ action.comment }}</p>
              <small>{{ dateTime(action.createdAt) }}</small>
            </div>
          </article>
        </div>
        <div v-else class="compact-empty">当前还没有操作记录</div>
      </section>
    </template>
  </main>
</template>
